/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useWindowSize, useDebounce, useInterval } from 'react-use';
import { MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from '../constants';
import { useUserQuery } from '../actions/UserActions';
import { parseTimeStr } from './time';
import { useStore, MINIMIZED_MENU } from '../store';
import { getSocket } from './socket';

export function useFolderPrefix({ user, organization }) {
  const location = useLocation();

  const [folderPrefix, isRootFolder, project] = useMemo(() => {
    const match = location.pathname.match(/\/folders\/(.*)/);
    if (match) {
      const isProject = match[1].split('/').length === 2;
      return [`/${match[1]}`, false, isProject];
    }

    if (user) {
      const prefix = user?.rootFolder?.id ? `/${user.rootFolder.id}` : '/';
      return [prefix, true, false];
    }

    if (organization) {
      const prefix = organization?.rootFolder?.id
        ? `/${organization.rootFolder.id}`
        : '/';
      return [prefix, true, false];
    }

    return ['/', true, false];
  }, [location.pathname, user, organization]);

  return { folderPrefix, isRootFolder, isProjectLevel: project };
}

export function useWindowSizeDebounced(ms = 250) {
  const { width, height } = useWindowSize();
  const [debouncedWidth, setDebouncedWidth] = useState(width);
  const [debouncedHeight, setDebouncedHeight] = useState(height);

  useDebounce(
    () => {
      setDebouncedWidth(width);
      setDebouncedHeight(height);
    },
    ms,
    [width, height]
  );

  return { width: debouncedWidth, height: debouncedHeight };
}

export function useIsMobile() {
  const { width } = useWindowSizeDebounced();
  const isMobile = width < MOBILE_BREAKPOINT;
  const isTablet = width < TABLET_BREAKPOINT;
  return { isMobile, isTablet, width };
}

export function useBoundingClientRectDebounced(elRef, ms = 250) {
  const [bcr, setBcr] = useState({});
  const { height } = useWindowSizeDebounced();

  function compareBcr(bcr1, bcr2) {
    return (
      bcr1.top === bcr2.top &&
      bcr1.bottom === bcr2.bottom &&
      bcr1.left === bcr2.left &&
      bcr1.right === bcr2.right &&
      bcr1.width === bcr2.width &&
      bcr1.height === bcr2.height
    );
  }

  useInterval(() => {
    if (elRef.current) {
      const newBcr = elRef.current.getBoundingClientRect();

      if (!compareBcr(newBcr, bcr)) {
        setBcr(newBcr);
      }
    }
  }, ms);

  useDebounce(
    () => {
      if (elRef.current) {
        const newBcr = elRef.current.getBoundingClientRect();

        if (!compareBcr(newBcr, bcr)) {
          setBcr(elRef.current.getBoundingClientRect());
        }
      }
    },
    ms,
    [height, bcr]
  );

  return {
    top: bcr.top || 0,
    bottom: bcr.bottom || 0,
    left: bcr.left || 0,
    right: bcr.right || 0,
    width: bcr.width || 0,
    height: bcr.height || 0,
  };
}

export function useWindowSizeFn(fn, ms = 250, deps) {
  const { width, height } = useWindowSizeDebounced(ms);

  return useMemo(
    () => fn({ width, height }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [width, height, fn, ...deps]
  );
}

export function useKeypoints(meeting) {
  const keypoints = useMemo(() => {
    if (!meeting?.textAnalysis?.result?.keypoints) {
      return [];
    }
    let k = meeting.textAnalysis.result.keypoints;

    if (Array.isArray(k) && k.length === 1) {
      k = k[0];
    }

    try {
      // captures consecutive arrays and packs into single
      try {
        k = k.replaceAll(new RegExp(`\n][\n ]*\\[\n`, 'g'), ',');
        k = k.replaceAll(
          new RegExp(`(?<!")(title|start|language)(?!")`, 'g'),
          '"$1"'
        );
      } catch (err) {
        return [];
      }

      try {
        let k2 = JSON.parse(k);
        if (Array.isArray(k2)) {
          return k2;
        }
      } catch (err) {}

      const result = [];
      k.split(/({[^{]*})/).forEach((line) => {
        try {
          if (!line) return;
          // Replace all single quotes with double, but only if they are not inside a string
          line = line.replace(/([^"]|^)'([^"]|$)/g, '$1"$2');
          let parsed = JSON.parse(line);
          if (parsed && typeof parsed === 'object') {
            result.push(parsed);
          }
        } catch (err) {}
      });
      return result;
    } catch (err) {
      return [];
    }
  }, [meeting]);

  const supported = useMemo(() => {
    try {
      const re = new RegExp(`(?<!")(title|start|language)(?!")`, 'g');
      return true;
    } catch (err) {
      return false;
    }
  }, []);

  return {
    keypoints: keypoints.filter((x) => x?.title && x?.start),
    supported,
  };
}

export function getQA(meeting, text) {
  let k = text || meeting?.textAnalysis?.result?.qa;
  if (!k) return [];

  if (Array.isArray(k) && k.length === 1) {
    k = k[0];
  }

  try {
    // Output might be many arrays in one string
    // e.g [ object, object], [object]
    // Finds these and packs them into one array
    const arrays = k.match(/\[[^\]]+\]/g);
    if (arrays?.length > 1) {
      k = arrays.map((x) => x.slice(1, -1)).join(',');
      k = `[${k}]`;
    }

    try {
      let k2 = JSON.parse(k);
      if (Array.isArray(k2)) {
        return k2;
      }
    } catch (err) {}
    const result = [];
    k = k.replaceAll(/}\n+{/g, '},{');

    const lines = k.split(/},\n*{/);

    lines.forEach((line, idx) => {
      if (idx === 0) {
        line = `${line}}`;
      } else if (idx === lines.length - 1) {
        line = `{${line}`;
      } else {
        line = `{${line}}`;
      }

      try {
        if (!line) return;
        let parsed = JSON.parse(line);
        if (!Array.isArray(parsed)) {
          result.push(parsed);
        } else {
          result.push(...parsed);
        }
      } catch (err) {}
    });
    return result;
  } catch (err) {
    return [];
  }
}

export function useQA(meeting, text) {
  const qa = useMemo(() => {
    return getQA(meeting, text);
  }, [meeting]);
  return qa;
}

function noop() {}
export function useTextWithTimeLinks(content, onScrollToTime = noop) {
  return useMemo(() => {
    if (!content) {
      return null;
    }
    if (Array.isArray(content)) {
      content = content.join('\n');
    }
    const msg = content.toString().trim();

    // Find all occurences of time (HH:mm or HH:mm:ss) and wrap them in a span. All other text is left as is.
    const timeRegex =
      /\b((?:[0-9]{1,2}:)?[0-5]?[0-9]:[0-5][0-9](?:[.][0-9]{1,2})?)\b/g;
    const parts = msg.split(timeRegex);

    return parts.map((part, i) => {
      if (timeRegex.test(part)) {
        return (
          <span
            key={i}
            css={css`
              color: var(--primary-main);
              cursor: pointer;
            `}
            onClick={() => onScrollToTime(parseTimeStr(part))}
          >
            {part}
          </span>
        );
      } else {
        return React.createElement(React.Fragment, { key: i }, part);
      }
    });
  }, [content, onScrollToTime]);
}

export function useIsGuest() {
  const { user } = useUserQuery();
  const isGuest = !user || !user.role || user.role === 'guest';
  return isGuest;
}

export function useIsMinimizedMenu() {
  const [isMinimizedMenu, _] = useStore(MINIMIZED_MENU);
  return { isMinimizedMenu };
}

export function useIsOccupied(assetId, assetType) {
  const [isOccupied, setIsOccupied] = useState(false);

  useEffect(() => {
    const socket = getSocket();
    function onConnect() {
      if (assetType === 'transcript') {
        socket.emit('join-transcript', {
          meetingId: assetId,
        });
      }
    }

    function onOccupied() {
      setIsOccupied(true);
    }

    function onAvailable() {
      setIsOccupied(false);
    }

    socket.on('occupied', onOccupied);
    socket.on('available', onAvailable);
    socket.on('connect', onConnect);

    return () => {
      socket.off('connect', onConnect);
      socket.off('occupied', onOccupied);
      socket.off('available', onAvailable);
      if (assetId && assetType === 'transcript') {
        socket.emit('leave', assetId);
      }
    };
  }, [assetId, assetType]);

  return {
    isOccupied,
  };
}

export function useRestHeight(triggers = [], offset = 16, initialHeight = 500) {
  const [ref, setRef] = useState(null);
  const [restHeight, setRestHeight] = useState(initialHeight);

  useEffect(() => {
    function onResize() {
      const rect = ref.getBoundingClientRect();
      setRestHeight(window.innerHeight - rect.top - offset);
    }
    if (ref) {
      const rect = ref.getBoundingClientRect();
      setRestHeight(window.innerHeight - rect.top - offset);

      window.addEventListener('resize', onResize);
    }

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [ref?.clientWidth, ref?.clientHeight, offset, ...triggers]);

  return { ref, setRef, maxHeight: restHeight, height: restHeight };
}

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function useUserName(user) {
  if (user?.onboarding?.personalInfo?.name) {
    let split = user.onboarding.personalInfo.name
      .split(' ')
      .filter((x) => x.trim());
    if (split.length > 0) {
      return capitalize(split[0]);
    }
  }
  if (user?.onboarding?.companyInfo?.fullName) {
    let split = user.onboarding.companyInfo.fullName
      .split(' ')
      .filter((x) => x.trim());
    if (split.length > 0) {
      return capitalize(split[0]);
    }
  }
  // Check if email may contain name like (a.b@domain)
  let split = user?.email?.split('@')[0].split('.');
  if (split.length === 2) {
    return capitalize(split[0]);
  }
  if (user?.email) {
    return user.email.split('@')?.[0];
  }
  return user?.email;
}

export function useAssetWidth(ref, flexBasis, margin = 8) {
  const { isMobile } = useIsMobile();
  const [assetWidth, setAssetWidth] = useState(undefined);

  function set() {
    const refWidth = ref?.clientWidth;
    if (!refWidth) return;
    let b = flexBasis ? flexBasis : isMobile ? 150 : 239;
    const cols = Math.floor(refWidth / b);
    setAssetWidth(Math.floor(refWidth / cols) - margin);
  }

  useEffect(() => {
    if (!ref) return;
    const observer = new ResizeObserver(() => {
      set();
    });
    observer.observe(ref);
    set();
    return () => {
      observer.disconnect();
    };
  }, [ref, isMobile, margin]);

  return assetWidth;
}

export const useResizable = ({
  initialSize = 300,
  minSize = 200,
  maxSize = window.innerWidth - 200,
  direction = 'horizontal',
  getParent = () => window,
  dragOffset = 0,
  storageKey, // If set, we store the size in local storage
} = {}) => {
  const [size, setSize] = useState(
    storageKey ? localStorage.getItem(storageKey) || initialSize : initialSize
  );
  const [isResizing, setIsResizing] = useState(false);
  const elementRef = useRef(null);
  const resizerRef = useRef(null);

  const saveSize = (newSize) => {
    if (storageKey) {
      localStorage.setItem(storageKey, newSize);
    }
  };

  // Debounce the save
  const [, cancel] = useDebounce(
    () => {
      saveSize(size);
    },
    1000,
    [size]
  );

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !elementRef.current) return;

      // Prevent text selection during resize
      e.preventDefault();

      let newSize;

      const parentRect = getParent(elementRef.current)?.getBoundingClientRect();
      if (direction === 'horizontal') {
        // Horizontal resizing (left-right)
        newSize = parentRect.right - e.clientX + dragOffset;
      } else {
        // Vertical resizing (top-bottom)
        newSize = e.clientY;
      }

      // Apply size constraints
      const constrainedSize = Math.max(minSize, Math.min(newSize, maxSize));

      setSize(constrainedSize);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    // Add event listeners when resizing
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    // Cleanup event listeners
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, direction, minSize, maxSize]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only start resizing on left mouse button
    if (e.button === 0) {
      setIsResizing(true);
    }
  };

  return {
    size,
    setSize,
    elementProps: {
      ref: elementRef,
      style:
        direction === 'horizontal'
          ? { width: `${size}px` }
          : { height: `${size}px` },
    },
    resizerProps: {
      ref: resizerRef,
      onMouseDown: handleMouseDown,
      style: {
        cursor: direction === 'horizontal' ? 'col-resize' : 'row-resize',
      },
    },
    isResizing,
  };
};

export function useClientLanguages() {
  const { t } = useTranslation();

  return useMemo(
    () => [
      { label: t('Engelska'), value: 'en' },
      { label: t('Tyska'), value: 'de' },
      { label: t('Svenska'), value: 'sv' },
      { label: t('Norska'), value: 'no' },
      { label: t('Danska'), value: 'da' },
    ],
    [t]
  );
}

export function useSupportedLanguages() {
  const { t } = useTranslation();

  return useMemo(
    () => ({
      auto: t('Automatisk'),
      swe: t('Svenska'),
      eng: t('Engelska'),
      dan: t('Danska'),
      nob: t('Norska (Bokmål)'),
      deu: t('Tyska'),
      rus: t('Ryska'),
      ukr: t('Ukrainska'),
      fra: t('Franska'),
      spa: t('Spanska'),
      ita: t('Italienska'),
      por: t('Portugisiska'),
      tur: t('Turkiska'),
      fin: t('Finska'),
      nld: t('Nederländska'),
      ell: t('Grekiska'),
      pol: t('Polska'),
    }),
    [t]
  );
}
